import React, { useState } from 'react';
import { useQuery } from 'react-query';
import apiClient from '../config/axios';
import toast from 'react-hot-toast';
import {
  Plus,
  MessageSquare,
  Search,
  GitBranch,
  Square,
  Save,
  Trash2,
  Edit,
  ArrowRight,
  FileText,
  Loader2,
  CheckSquare
} from 'lucide-react';

import { useWorkspace } from '../contexts/WorkspaceContext';

const ChatbotFlowBuilder = ({ chatbot, onSave }) => {
  const { currentWorkspace } = useWorkspace();
  const [flowSteps, setFlowSteps] = useState(chatbot?.flow_steps ? (
    typeof chatbot.flow_steps === 'string' ? JSON.parse(chatbot.flow_steps) : chatbot.flow_steps
  ) : []);
  const [selectedStep, setSelectedStep] = useState(null);
  const [showStepModal, setShowStepModal] = useState(false);
  const [editorMode, setEditorMode] = useState('create'); // 'create' | 'edit'
  const [isDirty, setIsDirty] = useState(false);
  const [selectedStepIds, setSelectedStepIds] = useState([]);

  // Fetch Facebook (WhatsApp Business) templates for message steps
  const { data: facebookTemplatesData } = useQuery(
    ['chatbot-facebook-templates', currentWorkspace?.id],
    async () => {
      if (!currentWorkspace?.id) {
        return { templates: [] };
      }
      const headers = { 'x-workspace-id': currentWorkspace.id };
      const response = await apiClient.get('/api/whatsapp/templates?days=365&useCache=true', { headers });
      return response.data;
    },
    { staleTime: 5 * 60 * 1000, enabled: !!currentWorkspace?.id }
  );

  const allTemplates = (facebookTemplatesData?.templates || []).map((template, index) => ({
    ...template,
    id: template.id || `facebook_${template.name}_${index}`,
    type: 'facebook'
  }));

  // Fetch WhatsApp Flows (Meta Flows) for \"Use Flow\" message type
  const { data: flowsData } = useQuery(
    ['whatsapp-flows', currentWorkspace?.id],
    async () => {
      if (!currentWorkspace?.id) {
        return { flows: [] };
      }
      const headers = { 'x-workspace-id': currentWorkspace.id };
      const res = await apiClient.get('/api/whatsapp/wflow', { headers });
      return res.data;
    },
    { enabled: !!currentWorkspace?.id }
  );

  const allFlows = flowsData?.flows || [];

  // Step types available for chatbots
  const stepTypes = [
    {
      id: 'message',
      name: 'Send Message',
      description: 'Send a message to the user',
      icon: MessageSquare,
      color: 'bg-blue-500'
    },
    {
      id: 'keyword_match',
      name: 'Keyword Match',
      description: 'Check if message contains specific keywords',
      icon: Search,
      color: 'bg-green-500'
    }
  ];

  // Quick flow templates to make designing flows easier
  const handleApplyPreset = (presetId) => {
    if (flowSteps.length > 0) {
      const confirmReplace = window.confirm(
        'Applying a template will replace your current steps. Do you want to continue?'
      );
      if (!confirmReplace) return;
    }

    if (presetId === 'three_option_menu') {
      const newSteps = createThreeOptionMenuPreset();
      setFlowSteps(newSteps);
      setSelectedStep(null);
      setShowStepModal(false);
      setIsDirty(true);
      toast.success('3-option main menu flow applied. You can edit any step.');
    }
  };

  const handleAddStep = (type) => {
    const stepTemplate = getStepTemplate(type);
    const newStep = {
      ...stepTemplate,
      id: `step_${Date.now()}`,
      name: `${stepTemplate.name} ${flowSteps.length + 1}`
    };

    // Don't add to flow until user saves
    setSelectedStep(newStep);
    setEditorMode('create');
    setShowStepModal(true);
  };

  const handleEditStep = (step) => {
    setSelectedStep(step);
    setEditorMode('edit');
    setShowStepModal(true);
  };

  // Helper: delete one or more steps and clean references
  const deleteStepsByIds = (idsToDelete) => {
    if (!idsToDelete || idsToDelete.length === 0) return;
    const deleteSet = new Set(idsToDelete);

    const updatedSteps = flowSteps.filter(step => !deleteSet.has(step.id));

    const cleanedSteps = updatedSteps.map(step => {
      let updated = { ...step };

      if (updated.next_step_id && deleteSet.has(updated.next_step_id)) {
        updated.next_step_id = null;
      }
      if (updated.on_match && deleteSet.has(updated.on_match)) {
        updated.on_match = null;
      }
      if (updated.on_no_match && deleteSet.has(updated.on_no_match)) {
        updated.on_no_match = null;
      }
      if (updated.on_true && deleteSet.has(updated.on_true)) {
        updated.on_true = null;
      }
      if (updated.on_false && deleteSet.has(updated.on_false)) {
        updated.on_false = null;
      }

      return updated;
    });

    setFlowSteps(cleanedSteps);
    setSelectedStepIds(prev => prev.filter(id => !deleteSet.has(id)));
    setIsDirty(true);
  };

  const handleDeleteStep = (stepId) => {
    if (window.confirm('Are you sure you want to delete this step?')) {
      deleteStepsByIds([stepId]);
    }
  };

  const handleToggleStepSelect = (stepId) => {
    setSelectedStepIds(prev =>
      prev.includes(stepId)
        ? prev.filter(id => id !== stepId)
        : [...prev, stepId]
    );
  };

  const handleSelectAllSteps = () => {
    if (selectedStepIds.length === flowSteps.length) {
      setSelectedStepIds([]);
    } else {
      setSelectedStepIds(flowSteps.map(step => step.id));
    }
  };

  const handleBulkDeleteSelectedSteps = () => {
    if (selectedStepIds.length === 0) return;
    if (!window.confirm(`Are you sure you want to delete ${selectedStepIds.length} step(s)? This cannot be undone.`)) {
      return;
    }
    deleteStepsByIds(selectedStepIds);
  };

  const handleUpdateStep = (updatedStep) => {
    const exists = flowSteps.some(step => step.id === updatedStep.id);
    const updatedSteps = exists
      ? flowSteps.map(step => (step.id === updatedStep.id ? updatedStep : step))
      : [...flowSteps, updatedStep];

    setFlowSteps(updatedSteps);
    setShowStepModal(false);
    setSelectedStep(null);
    setIsDirty(true);
  };

  const handleSave = () => {
    const chatbotData = {
      flow_steps: flowSteps,
      status: chatbot.status
    };
    onSave(chatbotData);
    setIsDirty(false);
  };

  const getStepTemplate = (type) => {
    const templates = {
      message: {
        type: 'message',
        name: 'Send Message',
        message_text: '',
        template_id: null,
        language: 'auto',
        next_step_id: null,
        delay: 0,
        limit_frequency_24h: true
      },
      keyword_match: {
        type: 'keyword_match',
        name: 'Keyword Match',
        keywords: [],
        match_type: 'contains',
        case_sensitive: false,
        on_match: null,
        on_no_match: null
      },
      condition: {
        type: 'condition',
        name: 'Condition',
        condition: {
          field: '',
          operator: 'exists',
          value: ''
        },
        on_true: null,
        on_false: null
      },
      end: {
        type: 'end',
        name: 'End Conversation',
        message_text: 'Thank you for chatting with us!'
      },
      // For existing flows that were saved as whatsapp_flow,
      // treat them like a specialized message step.
      whatsapp_flow: {
        type: 'whatsapp_flow',
        name: 'Send WhatsApp Flow',
        message_text: '',
        template_id: null,
        flow_id: null,
        flow_cta: 'Open',
        flow_mode: 'published',
        screen: null,
        body_text: '',
        limit_frequency_24h: true
      }
    };
    return templates[type] || templates.message;
  };

  const getStepIcon = (type) => {
    const icons = {
      message: MessageSquare,
      whatsapp_flow: MessageSquare,
      keyword_match: Search,
      condition: GitBranch,
      end: Square
    };
    return icons[type] || MessageSquare;
  };

  const getStepColor = (type) => {
    const colors = {
      message: 'bg-blue-100 border-blue-300 text-blue-800',
      whatsapp_flow: 'bg-indigo-100 border-indigo-300 text-indigo-800',
      keyword_match: 'bg-green-100 border-green-300 text-green-800',
      condition: 'bg-yellow-100 border-yellow-300 text-yellow-800',
      end: 'bg-red-100 border-red-300 text-red-800'
    };
    return colors[type] || 'bg-gray-100 border-gray-300 text-gray-800';
  };

  // Preset: 3-option main menu flow (Services / Pricing / Support)
  const createThreeOptionMenuPreset = () => {
    const stepWelcomeId = 'step_welcome_main_menu';
    const stepServicesRouterId = 'step_menu_services';
    const stepPricingRouterId = 'step_menu_pricing';
    const stepSupportRouterId = 'step_menu_support';
    const stepServicesAnswerId = 'step_services_answer';
    const stepPricingAnswerId = 'step_pricing_answer';
    const stepSupportAnswerId = 'step_support_answer';
    const stepInvalidChoiceId = 'step_invalid_choice';
    const stepEndId = 'step_end_conversation';

    return [
      {
        id: stepWelcomeId,
        type: 'message',
        name: 'Welcome',
        message_text:
          'Hi! How can we help you today?\n1) Services\n2) Pricing\n3) Support\n\nReply with 1, 2, or 3.',
        template_id: null,
        language: 'auto',
        next_step_id: stepServicesRouterId,
        delay: 0
      },
      {
        id: stepServicesRouterId,
        type: 'keyword_match',
        name: 'Menu – Services?',
        keywords: ['1', 'services'],
        match_type: 'contains',
        case_sensitive: false,
        on_match: stepServicesAnswerId,
        on_no_match: stepPricingRouterId
      },
      {
        id: stepPricingRouterId,
        type: 'keyword_match',
        name: 'Menu – Pricing?',
        keywords: ['2', 'pricing'],
        match_type: 'contains',
        case_sensitive: false,
        on_match: stepPricingAnswerId,
        on_no_match: stepSupportRouterId
      },
      {
        id: stepSupportRouterId,
        type: 'keyword_match',
        name: 'Menu – Support?',
        keywords: ['3', 'support'],
        match_type: 'contains',
        case_sensitive: false,
        on_match: stepSupportAnswerId,
        on_no_match: stepInvalidChoiceId
      },
      {
        id: stepServicesAnswerId,
        type: 'message',
        name: 'Services Answer',
        message_text:
          'We offer Digital Marketing, Website Development, Software & App Development.',
        template_id: null,
        language: 'auto',
        next_step_id: stepEndId,
        delay: 0
      },
      {
        id: stepPricingAnswerId,
        type: 'message',
        name: 'Pricing Answer',
        message_text: 'Our pricing starts from...',
        template_id: null,
        language: 'auto',
        next_step_id: stepEndId,
        delay: 0
      },
      {
        id: stepSupportAnswerId,
        type: 'message',
        name: 'Support Answer',
        message_text: 'For support, you can reach us and our team will help you.',
        template_id: null,
        language: 'auto',
        next_step_id: stepEndId,
        delay: 0
      },
      {
        id: stepInvalidChoiceId,
        type: 'message',
        name: 'Invalid Choice',
        message_text:
          "Sorry, I didn't understand that. Please reply with 1, 2, or 3.",
        template_id: null,
        language: 'auto',
        next_step_id: stepWelcomeId,
        delay: 0
      },
      {
        id: stepEndId,
        type: 'end',
        name: 'End Conversation',
        message_text: 'Thank you for chatting with us!'
      }
    ];
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Flow Builder</h3>
          <p className="text-sm text-gray-600">
            Design your chatbot conversation flow with message steps, conditions, and keyword matching.
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={handleSave}
            disabled={!isDirty}
            className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md ${isDirty
              ? 'text-white bg-blue-600 hover:bg-blue-700'
              : 'text-gray-400 bg-gray-200 cursor-not-allowed'
              }`}
          >
            <Save className="mr-2 h-4 w-4" />
            Save Changes
          </button>
        </div>
      </div>

      {/* Quick Templates */}
      <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
        <h4 className="text-sm font-medium text-gray-900 mb-2">Quick Flow Templates</h4>
        <p className="text-xs text-gray-600 mb-3">
          Use a ready-made flow so you don&apos;t have to design from scratch. You can edit all steps after applying a template.
        </p>
        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => handleApplyPreset('three_option_menu')}
            className="inline-flex items-center px-3 py-2 border border-blue-300 rounded-md text-xs font-medium text-blue-700 bg-white hover:bg-blue-50"
          >
            <FileText className="h-4 w-4 mr-2" />
            3-Option Main Menu (1: Services, 2: Pricing, 3: Support)
          </button>
        </div>
      </div>

      {/* Step Palette */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h4 className="text-sm font-medium text-gray-900 mb-3">Add Step</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {stepTypes.map((stepType) => {
            const Icon = stepType.icon;
            return (
              <button
                key={stepType.id}
                onClick={() => handleAddStep(stepType.id)}
                className="flex flex-col items-center p-3 border border-gray-200 rounded-lg hover:border-gray-300 hover:bg-white transition-colors"
              >
                <div className={`p-2 rounded-lg ${stepType.color} mb-2`}>
                  <Icon className="h-5 w-5 text-white" />
                </div>
                <span className="text-xs font-medium text-gray-900 text-center">
                  {stepType.name}
                </span>
                <span className="text-xs text-gray-500 text-center mt-1">
                  {stepType.description}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Flow Canvas */}
      <div className="bg-white border border-gray-200 rounded-lg min-h-96">
        {flowSteps.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <MessageSquare className="h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No steps yet</h3>
            <p className="text-gray-500 text-center mb-4">
              Start building your chatbot by adding message steps, conditions, and keyword matching.
            </p>
            <button
              onClick={() => handleAddStep('message')}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="mr-2 h-4 w-4" />
              Add First Step
            </button>
          </div>
        ) : (
          <div className="p-6 md:flex md:space-x-6">
            {/* Left: Flow steps list / editor */}
            <div className="md:w-[55%] lg:w-[55%]">
              {/* Bulk actions for steps */}
              <div className="flex items-center justify-between mb-4">
                <button
                  type="button"
                  onClick={handleSelectAllSteps}
                  className="flex items-center space-x-2 text-sm text-gray-700 hover:text-gray-900"
                >
                  {selectedStepIds.length === flowSteps.length && flowSteps.length > 0 ? (
                    <CheckSquare className="h-4 w-4" />
                  ) : (
                    <Square className="h-4 w-4" />
                  )}
                  <span>{selectedStepIds.length === flowSteps.length && flowSteps.length > 0 ? 'Clear selection' : 'Select all steps'}</span>
                </button>

                {selectedStepIds.length > 0 && (
                  <button
                    type="button"
                    onClick={handleBulkDeleteSelectedSteps}
                    className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md bg-red-600 text-white hover:bg-red-700"
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Delete selected ({selectedStepIds.length})
                  </button>
                )}
              </div>

              <div className="space-y-4">
                {flowSteps.map((step, index) => {
                  const Icon = getStepIcon(step.type);
                  const colorClass = getStepColor(step.type);

                  return (
                    <div key={step.id} className="flex items-center space-x-4">
                      {/* Step Number */}
                      <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                        <span className="text-sm font-medium text-gray-600">{index + 1}</span>
                      </div>

                      {/* Step Card */}
                      <div className={`flex-1 border rounded-lg p-4 ${colorClass}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="p-1.5 rounded bg-white bg-opacity-50">
                              <Icon className="h-4 w-4" />
                            </div>
                            <div>
                              <h4 className="font-medium text-gray-900">{step.name}</h4>
                              <p className="text-sm text-gray-600">
                                {getStepDescription(step)}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              type="button"
                              onClick={() => handleToggleStepSelect(step.id)}
                              className="p-1.5 text-gray-600 hover:text-gray-900 rounded"
                              title={selectedStepIds.includes(step.id) ? 'Unselect step' : 'Select step'}
                            >
                              {selectedStepIds.includes(step.id) ? (
                                <CheckSquare className="h-4 w-4" />
                              ) : (
                                <Square className="h-4 w-4" />
                              )}
                            </button>
                            <button
                              onClick={() => handleEditStep(step)}
                              className="p-1.5 text-gray-600 hover:text-gray-900 rounded"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteStep(step.id)}
                              className="p-1.5 text-gray-600 hover:text-red-600 rounded"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Connection Arrow */}
                      {index < flowSteps.length - 1 && (
                        <div className="flex-shrink-0">
                          <ArrowRight className="h-5 w-5 text-gray-400" />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Right: Conversation-style preview */}
            <div className="mt-8 md:mt-0 md:w-[45%] lg:w-[45%] border-t md:border-t-0 md:border-l border-gray-200 md:pl-6 pt-6 md:pt-0">
              <h4 className="text-sm font-medium text-gray-900 mb-2">Conversation Preview</h4>
              <p className="text-xs text-gray-500 mb-3">
                Example of how the customer&apos;s messages and your flow replies could look in a chat.
              </p>

              <div className="space-y-3 max-h-[480px] overflow-y-auto pr-1">
                {flowSteps.map((step, index) => {
                  const isMessageStep = step.type === 'message' || step.type === 'whatsapp_flow';

                  return (
                    <div key={step.id} className="space-y-1">
                      {/* Customer side placeholder for this step */}
                      <div className="flex justify-start">
                        <div className="max-w-[80%] rounded-2xl rounded-tl-md bg-gray-100 px-3 py-2 text-xs text-gray-800">
                          {index === 0
                            ? 'Customer sends a message that triggers this flow...'
                            : `Customer replies again (before "${step.name}")...`}
                        </div>
                      </div>

                      {/* Bot reply preview for steps that send messages/flows */}
                      {isMessageStep ? (
                        <div className="flex justify-end">
                          <div className="max-w-[80%]">
                            <ChatMessagePreview
                              messageText={step.type === 'whatsapp_flow'
                                ? (step.body_text || step.message_text || '')
                                : (step.message_text || '')}
                              buttonLabel={step.flow_id ? (step.flow_cta || 'Open') : null}
                            />
                          </div>
                        </div>
                      ) : (
                        <div className="flex justify-end">
                          <div className="max-w-[80%] rounded-2xl rounded-tr-md bg-blue-50 px-3 py-2 text-xs text-blue-900 border border-blue-100">
                            {step.type === 'keyword_match' && (
                              <span>
                                Routing step – checks message for keywords and then goes to the configured next step.
                              </span>
                            )}
                            {step.type === 'condition' && (
                              <span>
                                Condition step – branches based on a field/value and then goes to the configured next step.
                              </span>
                            )}
                            {step.type === 'end' && (
                              <span>
                                End conversation – flow stops after this point.
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Step Editor Modal */}
      {showStepModal && selectedStep && (
        <StepEditorModal
          step={selectedStep}
          mode={editorMode}
          templates={allTemplates}
          flows={allFlows}
          allSteps={flowSteps}
          onSave={handleUpdateStep}
          onClose={() => {
            setShowStepModal(false);
            setSelectedStep(null);
          }}
        />
      )}
    </div>
  );
};

// Helper function to get step description
const getStepDescription = (step) => {
  switch (step.type) {
    case 'message':
      return step.message_text ? `"${step.message_text.substring(0, 50)}..."` :
        step.template_id ? `Template: ${step.template_id}` : 'Send message';
    case 'whatsapp_flow':
      return step.flow_id ? `WhatsApp Flow: ${step.flow_id}` : 'Send WhatsApp Business Flow';
    case 'keyword_match':
      return `Check for: ${step.keywords?.join(', ') || 'keywords'}`;
    case 'condition':
      return `If ${step.condition?.field || 'field'} ${step.condition?.operator || 'exists'}`;
    case 'end':
      return step.message_text || 'End conversation';
    default:
      return 'Step configuration';
  }
};

// Shared input styles to match the rest of the app
const fieldInputClass =
  'mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500';
const fieldSelectClass = fieldInputClass;
const fieldTextareaClass = fieldInputClass;

// Template / message preview – shows how the chatbot response will look in chat
const ChatMessagePreview = ({ messageText, buttonLabel }) => {
  const text = (messageText || '').trim();
  if (!text && !buttonLabel) return null;
  const parts = text.split(/(\{\{\d+\}\})/g);
  return (
    <div className="rounded-lg border border-gray-200 bg-[#f5f0e8] p-4">
      <p className="text-xs font-medium text-gray-500 mb-2">Template Preview</p>
      <div className="flex justify-start">
        <div className="max-w-[85%] rounded-2xl rounded-tl-md bg-white/95 shadow-sm border border-gray-200/80 px-4 py-3">
          <div className="text-sm text-gray-800 whitespace-pre-wrap break-words">
            {parts.map((part, i) =>
              /^\{\{\d+\}\}$/.test(part) ? (
                <span key={i} className="inline-block mx-0.5 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-xs font-medium">
                  {part}
                </span>
              ) : (
                <span key={i}>{part}</span>
              )
            )}
          </div>
          {buttonLabel && (
            <div className="mt-3">
              <button
                type="button"
                className="w-full py-2 px-3 rounded-lg bg-blue-600 text-white text-sm font-medium cursor-default"
                disabled
              >
                {buttonLabel}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Step Editor Modal Component
const StepEditorModal = ({ step, mode = 'create', templates, flows = [], allSteps, onSave, onClose }) => {
  const [formData, setFormData] = useState({ ...step });
  const [isSaving, setIsSaving] = useState(false);

  // Local message type state to control the dropdown and UI:
  // 'text' | 'template' | 'flow'
  const [messageType, setMessageType] = useState(() => {
    if (step.type === 'whatsapp_flow' || step.flow_id) return 'flow';
    if (step.template_id) return 'template';
    return 'text';
  });

  // Helper to get a template by ID
  const getTemplateById = (id) => {
    if (!id) return null;
    return templates.find(t => (t.id || t.name) === id) || null;
  };

  // Extract parameter indices (1-based) from a template body/content
  const extractTemplateParamIndices = (template) => {
    if (!template) return [];
    let text = '';
    if (template.components && Array.isArray(template.components)) {
      const bodyComponent = template.components.find(c => c.type === 'BODY');
      if (bodyComponent && bodyComponent.text) {
        text = bodyComponent.text;
      }
    }
    if (!text) {
      text = template.content || template.text || '';
    }
    if (!text) return [];
    const indices = new Set();
    const regex = /\{\{(\d+)\}\}/g;
    let match;
    while ((match = regex.exec(text)) !== null) {
      const idx = parseInt(match[1], 10);
      if (!Number.isNaN(idx)) {
        indices.add(idx);
      }
    }
    return Array.from(indices).sort((a, b) => a - b);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSaving) return;
    try {
      setIsSaving(true);
      const result = onSave(formData);
      if (result && typeof result.then === 'function') {
        await result;
      }
    } finally {
      setIsSaving(false);
    }
  };

  const updateFormData = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const updateNestedField = (parent, field, value) => {
    setFormData({
      ...formData,
      [parent]: {
        ...formData[parent],
        [field]: value
      }
    });
  };

  const getAvailableSteps = () => {
    return allSteps.filter(s => s.id !== step.id);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto" style={{ marginTop: '0px' }}>
      <div className="relative w-full max-w-2xl bg-white rounded-lg shadow-lg max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <h3 className="text-lg font-medium text-gray-900">
            {mode === 'create' ? 'Add Step' : 'Edit Step'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
          >
            <span className="sr-only">Close</span>
            ×
          </button>
        </div>

        <div className="px-6 pb-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Info */}
            <div>
              <label className="block text-sm font-medium text-gray-700">Step Name</label>
              <input
                type="text"
                required
                className={fieldInputClass}
                value={formData.name || ''}
                onChange={(e) => updateFormData('name', e.target.value)}
              />
            </div>

            {/* Step Type Specific Fields */}
            {(step.type === 'message' || step.type === 'whatsapp_flow') && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Message Type</label>
                  <select
                    className={fieldSelectClass}
                    value={messageType}
                    onChange={(e) => {
                      const value = e.target.value;
                      setMessageType(value);
                      if (value === 'template') {
                        updateFormData('type', 'message');
                        updateFormData('flow_id', null);
                        updateFormData('message_text', '');
                      } else if (value === 'flow') {
                        updateFormData('type', 'whatsapp_flow');
                        updateFormData('template_id', null);
                      } else {
                        // text
                        updateFormData('type', 'message');
                        updateFormData('template_id', null);
                        updateFormData('flow_id', null);
                      }
                    }}
                  >
                    <option value="text">Custom Text</option>
                    <option value="template">Use Template</option>
                    <option value="flow">Use WhatsApp Flow</option>
                  </select>
                </div>

                {/* Template-based message */}
                {messageType === 'template' ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Template</label>
                    <select
                      className={fieldSelectClass}
                      value={formData.template_id || ''}
                      onChange={(e) => {
                        const newTemplateId = e.target.value;
                        const selectedTemplate = getTemplateById(newTemplateId);

                        // When template changes, initialize message text from template body/content
                        let baseText = formData.message_text || '';
                        if (selectedTemplate) {
                          if (selectedTemplate.components && Array.isArray(selectedTemplate.components)) {
                            const bodyComponent = selectedTemplate.components.find(c => c.type === 'BODY');
                            if (bodyComponent && bodyComponent.text) {
                              baseText = bodyComponent.text;
                            }
                          }
                          if (!baseText) {
                            baseText = selectedTemplate.content || selectedTemplate.text || '';
                          }
                        }

                        // Initialize parameter bindings for detected placeholders
                        const paramIndices = extractTemplateParamIndices(selectedTemplate);
                        const initialBindings = {};
                        paramIndices.forEach((idx) => {
                          initialBindings[idx] = formData.template_parameters?.[idx] || {
                            type: 'static',
                            value: ''
                          };
                        });

                        setFormData({
                          ...formData,
                          type: 'message',
                          template_id: newTemplateId || null,
                          message_text: baseText,
                          template_parameters: initialBindings
                        });
                      }}
                    >
                      <option value="">Select a template...</option>
                      {templates
                        .map(template => (
                          <option key={template.id} value={template.id}>
                            {template.name}
                          </option>
                        ))}
                    </select>

                    {/* Template parameters mapping */}
                    {formData.template_id && (() => {
                      const selectedTemplate = getTemplateById(formData.template_id);
                      const indices = extractTemplateParamIndices(selectedTemplate);
                      if (!indices.length) return null;

                      const bindings = formData.template_parameters || {};

                      return (
                        <div className="mt-4 space-y-2">
                          <p className="text-xs font-medium text-gray-700">Template Parameters</p>
                          {indices.map((idx) => {
                            const binding = bindings[idx] || { type: 'static', value: '' };
                            return (
                              <div key={idx} className="grid grid-cols-3 gap-2 items-center">
                                <div className="text-xs text-gray-600">
                                  Param {idx} ({`{{${idx}}}`})
                                </div>
                                <select
                                  className={fieldSelectClass + ' text-xs'}
                                  value={binding.type || 'static'}
                                  onChange={(e) => {
                                    const newType = e.target.value;
                                    const newBindings = {
                                      ...bindings,
                                      [idx]: { ...binding, type: newType }
                                    };
                                    updateFormData('template_parameters', newBindings);
                                  }}
                                >
                                  <option value="static">Static text</option>
                                  <option value="contact_name">Contact name</option>
                                </select>
                                {binding.type !== 'contact_name' && (
                                  <input
                                    type="text"
                                    className={fieldInputClass + ' text-xs'}
                                    value={binding.value || ''}
                                    onChange={(e) => {
                                      const newBindings = {
                                        ...bindings,
                                        [idx]: { ...binding, value: e.target.value }
                                      };
                                      updateFormData('template_parameters', newBindings);
                                    }}
                                    placeholder="Enter static value"
                                  />
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>
                ) : messageType === 'flow' ? (
                  // WhatsApp Flow-based message
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">WhatsApp Flow</label>
                      <select
                        className={fieldSelectClass}
                        value={formData.flow_id || ''}
                        onChange={(e) => updateFormData('flow_id', e.target.value)}
                      >
                        <option value="">Select a WhatsApp Flow...</option>
                        {flows.map((flow) => {
                          const status = (flow.status || '').toUpperCase();
                          const isDraft = status === 'DRAFT';
                          const label = flow.name || flow.id || 'Unnamed';
                          const statusLabel = status ? ` (${status})` : '';
                          return (
                            <option
                              key={flow.id}
                              value={flow.id}
                              disabled={isDraft}
                            >
                              {isDraft ? `${label} ${statusLabel} — not selectable` : `${label} ${statusLabel}`}
                            </option>
                          );
                        })}
                      </select>
                      <p className="mt-1 text-xs text-gray-500">
                        Only <span className="font-medium text-green-700">PUBLISHED</span> flows can be selected. Draft flows are shown for reference only.
                      </p>
                      {formData.flow_id && (() => {
                        const selected = flows.find((f) => f.id === formData.flow_id);
                        if (selected && (selected.status || '').toUpperCase() === 'DRAFT') {
                          return (
                            <p className="mt-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                              The selected flow is in <strong>DRAFT</strong>. Please choose a <strong>PUBLISHED</strong> flow for it to work.
                            </p>
                          );
                        }
                        return null;
                      })()}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Button Text (CTA)</label>
                      <input
                        type="text"
                        className={fieldInputClass}
                        value={formData.flow_cta || ''}
                        onChange={(e) => updateFormData('flow_cta', e.target.value)}
                        placeholder="Open"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Body text (message above button)</label>
                      <textarea
                        rows={3}
                        className={fieldTextareaClass}
                        value={formData.body_text || formData.message_text || ''}
                        onChange={(e) => {
                          updateFormData('body_text', e.target.value);
                          updateFormData('message_text', e.target.value);
                        }}
                        placeholder="e.g. Continue in WhatsApp flow to complete... (optional; if empty, a default message is used)"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        This is the text the user sees above the flow button. Leave blank to use the default: &quot;Continue in WhatsApp flow for [chatbot name]&quot;.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Message Text</label>
                    <textarea
                      rows={3}
                      className={fieldTextareaClass}
                      value={formData.message_text || ''}
                      onChange={(e) => updateFormData('message_text', e.target.value)}
                      placeholder="Enter the message to send..."
                    />
                  </div>
                )}

                {/* Template Preview – how the chatbot response will look in chat */}
                <ChatMessagePreview
                  messageText={
                    messageType === 'flow'
                      ? (formData.body_text || formData.message_text || '')
                      : (formData.message_text || '')
                  }
                  buttonLabel={messageType === 'flow' && (formData.flow_cta || 'Open') ? (formData.flow_cta || 'Open') : null}
                />

                {/* Frequency limit: always enforced as 1 send per 24h per contact */}
                <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                  <p className="text-sm font-medium text-gray-700">
                    Limit: 1 message per 24 hours (enabled)
                  </p>
                </div>

                {/* Next step: when user replies, which step runs next (e.g. Keyword Match for "thank you" → Send Message 2) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">After user replies, go to step</label>
                  <select
                    className={fieldSelectClass}
                    value={formData.next_step_id || ''}
                    onChange={(e) => updateFormData('next_step_id', e.target.value || null)}
                  >
                    <option value="">End conversation</option>
                    {getAvailableSteps().map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    When the customer sends another message, the flow continues from this step. Use a Keyword Match to route (e.g. &quot;thank you&quot; → thank you message, then End).
                  </p>
                </div>
              </div>
            )}

            {step.type === 'keyword_match' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Keywords</label>
                  <input
                    type="text"
                    className={fieldInputClass}
                    value={formData.keywords?.join(', ') || ''}
                    onChange={(e) => updateFormData('keywords', e.target.value.split(',').map(k => k.trim()))}
                    placeholder="hello, hi, hey"
                  />
                  <p className="mt-1 text-sm text-gray-500">Separate multiple keywords with commas</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Match Type</label>
                  <select
                    className={fieldSelectClass}
                    value={formData.match_type || 'contains'}
                    onChange={(e) => updateFormData('match_type', e.target.value)}
                  >
                    <option value="contains">Contains any keyword</option>
                    <option value="equals">Equals any keyword</option>
                    <option value="starts_with">Starts with any keyword</option>
                  </select>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="case_sensitive"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    checked={formData.case_sensitive || false}
                    onChange={(e) => updateFormData('case_sensitive', e.target.checked)}
                  />
                  <label htmlFor="case_sensitive" className="ml-2 block text-sm text-gray-900">
                    Case sensitive matching
                  </label>
                </div>

                {/* Routing configuration for keyword match */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">If matches, go to step</label>
                    <select
                      className={fieldSelectClass}
                      value={formData.on_match || ''}
                      onChange={(e) => updateFormData('on_match', e.target.value || null)}
                    >
                      <option value="">End conversation</option>
                      {getAvailableSteps().map(s => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-gray-500">
                      Select which step should run when the keyword condition matches.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">If no match, go to step</label>
                    <select
                      className={fieldSelectClass}
                      value={formData.on_no_match || ''}
                      onChange={(e) => updateFormData('on_no_match', e.target.value || null)}
                    >
                      <option value="">End conversation</option>
                      {getAvailableSteps().map(s => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-gray-500">
                      Optional: choose a fallback step if none of the keywords match.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {step.type === 'condition' && (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Field</label>
                    <input
                      type="text"
                      className={fieldInputClass}
                      value={formData.condition?.field || ''}
                      onChange={(e) => updateNestedField('condition', 'field', e.target.value)}
                      placeholder="user_name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Operator</label>
                    <select
                      className={fieldSelectClass}
                      value={formData.condition?.operator || 'exists'}
                      onChange={(e) => updateNestedField('condition', 'operator', e.target.value)}
                    >
                      <option value="exists">Exists</option>
                      <option value="equals">Equals</option>
                      <option value="contains">Contains</option>
                      <option value="greater_than">Greater Than</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Value</label>
                    <input
                      type="text"
                      className={fieldInputClass}
                      value={formData.condition?.value || ''}
                      onChange={(e) => updateNestedField('condition', 'value', e.target.value)}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">If True</label>
                    <select
                      className={fieldSelectClass}
                      value={formData.on_true || ''}
                      onChange={(e) => updateFormData('on_true', e.target.value)}
                    >
                      <option value="">End conversation</option>
                      {getAvailableSteps().map(s => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">If False</label>
                    <select
                      className={fieldSelectClass}
                      value={formData.on_false || ''}
                      onChange={(e) => updateFormData('on_false', e.target.value)}
                    >
                      <option value="">End conversation</option>
                      {getAvailableSteps().map(s => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}

            {step.type === 'end' && (
              <div>
                <label className="block text-sm font-medium text-gray-700">Final Message</label>
                <textarea
                  rows={3}
                  className={fieldTextareaClass}
                  value={formData.message_text || ''}
                  onChange={(e) => updateFormData('message_text', e.target.value)}
                  placeholder="Thank you for chatting with us!"
                />
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-4 border-t">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className={`inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${isSaving ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
                  }`}
              >
                {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {mode === 'create' ? 'Add Step' : 'Save Step'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ChatbotFlowBuilder;